import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../common/context/AuthContext';
import {
  Lock, Shield, UserCog, CheckCircle2, ChevronRight,
  Key, FileText, Edit2, X, Loader2, Eye, EyeOff, Smartphone,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { API_URL } from '../../../api/config';

// ── Small inline modal ────────────────────────────────────────────────────────
function Modal({ title, subtitle, accentColor = 'blue', onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-100">
        <div className={`bg-gradient-to-br ${accentColor === 'red' ? 'from-red-600 to-red-700' : accentColor === 'green' ? 'from-green-600 to-green-700' : 'from-blue-600 to-blue-700'} p-6 text-white relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
          <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center mb-3">
            <Lock size={22} />
          </div>
          <h2 className="text-lg font-bold">{title}</h2>
          {subtitle && <p className="text-white/70 text-xs mt-1">{subtitle}</p>}
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ── 4-box PIN input ───────────────────────────────────────────────────────────
function PinBoxes({ value, onChange, color = 'blue' }) {
  const refs = useRef([]);
  const handleChange = (i, v) => {
    if (!/^\d*$/.test(v)) return;
    const next = [...value]; next[i] = v.slice(-1); onChange(next);
    if (v && i < 3) refs.current[i + 1]?.focus();
  };
  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) refs.current[i - 1]?.focus();
  };
  return (
    <div className="flex gap-3 justify-center">
      {value.map((d, i) => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl outline-none transition-all
            ${d ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200'} focus:border-blue-500 focus:ring-2 focus:ring-blue-100`}
        />
      ))}
    </div>
  );
}

const MANAGER_FEATURES = [
  { id: 'sales_reports', label: 'Sales Reports' },
  { id: 'expense_reports', label: 'Expense Reports' },
  { id: 'gst_reports', label: 'GST Reports' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'billing', label: 'Billing' },
  { id: 'employee_management', label: 'Employee Management' },
  { id: 'user_management', label: 'User Management' },
  { id: 'settings', label: 'Settings' },
  { id: 'analytics', label: 'Analytics' },
];

const PinProtectionPage = () => {
  const navigate  = useNavigate();
  const { userToken: token, updateUserData } = useAuth();

  // ── State ─────────────────────────────────────────────────────────────────
  const [pinProtectionEnabled, setPinProtectionEnabled] = useState(false);
  const [hasAdminPin, setHasAdminPin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [hasManagerPin, setHasManagerPin] = useState(false);
  const [hasOutletPin, setHasOutletPin] = useState(false);
  
  const [managerFullAccess, setManagerFullAccess] = useState(true);
  const [restrictedFeatures, setRestrictedFeatures] = useState([]);

  // Outlet PIN modal: 'set-password' | 'set-pin' | 'set-confirm' | 'remove-password' | null
  const [outletPinModal, setOutletPinModal] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [newPin, setNewPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  
  // Security Verification Modal
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyPin, setVerifyPin] = useState(['', '', '', '']);
  const [verifyOtp, setVerifyOtp] = useState('');
  const [verifyOtpSent, setVerifyOtpSent] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [onVerifiedCallback, setOnVerifiedCallback] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchPinSettings = async () => {
    setLoading(true);
    try {
      const [pinRes, outletPinRes] = await Promise.all([
        fetch(`${API_URL}/pin-settings`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/auth/outlet-pin-status`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (pinRes.ok) {
        const d = await pinRes.json();
        if (d.success && d.data) {
          setPinProtectionEnabled(d.data.pinProtectionEnabled || false);
          setHasAdminPin(d.data.hasAdminPin || false);
          setAdminEmail(d.data.adminEmail || '');
          setHasManagerPin(d.data.hasManagerPin || false);
          setManagerFullAccess(d.data.managerFullAccess !== false);
          setRestrictedFeatures(d.data.restrictedFeatures || []);
        }
      }
      if (outletPinRes.ok) {
        const d = await outletPinRes.json();
        setHasOutletPin(d.data?.pinEnabled || false);
      }
    } catch (e) {
      console.error('Error fetching PIN settings:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPinSettings(); }, []);

  // ── Toggle report protection ───────────────────────────────────────────────
  const handleToggleProtection = async (enabled) => {
    if (enabled && !hasAdminPin) { navigate('/setup-admin-pin'); return; }
    try {
      const res = await fetch(`${API_URL}/pin-settings/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (res.ok) {
        setPinProtectionEnabled(enabled);
        toast.success(enabled ? 'PIN protection enabled' : 'PIN protection disabled');
      } else {
        toast.error('Failed to update PIN protection');
      }
    } catch {
      toast.error('Failed to update PIN protection');
    }
  };

  // ── Verification modal actions ─────────────────────────────────────────────
  const triggerVerification = (callback) => {
    setVerifyPin(['', '', '', '']);
    setVerifyOtp('');
    setVerifyOtpSent(false);
    setVerifyError('');
    setOnVerifiedCallback(() => callback);
    setShowVerifyModal(true);
  };

  const handleSendVerifyOtp = async () => {
    if (!adminEmail) {
      setVerifyError('No admin email set to receive OTP');
      return;
    }
    setVerifyLoading(true);
    try {
      const res = await fetch(`${API_URL}/pin-settings/send-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: adminEmail, role: 'admin' })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setVerifyOtpSent(true);
        toast.success(`Verification code sent to ${adminEmail}`);
      } else {
        setVerifyError(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setVerifyError('Network error. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleVerifyAction = async () => {
    setVerifyLoading(true);
    setVerifyError('');
    try {
      const pinStr = verifyPin.join('');
      const credentials = {};
      if (pinStr.length === 4) {
        credentials.pin = pinStr;
      } else if (verifyOtp.trim()) {
        credentials.otp = verifyOtp.trim();
      } else {
        setVerifyError('Enter Admin PIN or OTP code to verify');
        setVerifyLoading(false);
        return;
      }

      if (onVerifiedCallback) {
        await onVerifiedCallback(credentials);
      }
      setShowVerifyModal(false);
      setOnVerifiedCallback(null);
    } catch (err) {
      setVerifyError(err.message || 'Verification failed');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSavePermissions = (credentials) => {
    return new Promise(async (resolve, reject) => {
      setSaving(true);
      try {
        const response = await fetch(`${API_URL}/pin-settings/save-manager-permissions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            managerFullAccess,
            restrictedFeatures,
            pin: credentials.pin,
            otp: credentials.otp
          })
        });

        const data = await response.json();
        if (response.ok && data.success) {
          toast.success('Settings saved successfully. The manager cannot access the selected features.');
          await updateUserData({
            managerFullAccess,
            restrictedFeatures
          });
          resolve();
        } else {
          reject(new Error(data.message || 'Failed to save permissions'));
        }
      } catch (error) {
        reject(error);
      } finally {
        setSaving(false);
      }
    });
  };

  const toggleFeatureRestriction = (featureId) => {
    setRestrictedFeatures(prev => {
      if (prev.includes(featureId)) {
        return prev.filter(id => id !== featureId);
      } else {
        return [...prev, featureId];
      }
    });
  };

  // ── Outlet PIN set/remove flows ────────────────────────────────────────────
  const openSetFlow = () => { resetPinState(); setOutletPinModal('set-password'); };
  const openRemoveFlow = () => { resetPinState(); setOutletPinModal('remove-password'); };
  const closeModal = () => { resetPinState(); setOutletPinModal(null); };

  const resetPinState = () => {
    setPassword(''); setShowPassword(false);
    setNewPin(['', '', '', '']); setConfirmPin(['', '', '', '']);
  };

  const handleVerifyPassword = async (nextStep) => {
    if (!password) { toast.error('Please enter your password'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/verify-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setOutletPinModal(nextStep);
      } else {
        toast.error(data.message || 'Incorrect password');
      }
    } catch {
      toast.error('Failed to verify password');
    } finally {
      setLoading(false);
    }
  };

  const handleSetOutletPin = async () => {
    const pinStr = newPin.join('');
    const confirmStr = confirmPin.join('');
    if (pinStr.length !== 4) { toast.error('Please enter a 4-digit PIN'); return; }
    if (pinStr !== confirmStr) { toast.error('PINs do not match'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/set-pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinStr, password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Outlet PIN set successfully');
        setHasOutletPin(true);
        closeModal();
      } else {
        toast.error(data.message || 'Failed to set outlet PIN');
      }
    } catch {
      toast.error('Failed to set outlet PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveOutletPin = async () => {
    if (!password) { toast.error('Please enter your password'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/remove-pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Outlet PIN removed');
        setHasOutletPin(false);
        closeModal();
      } else {
        toast.error(data.message || 'Failed to remove outlet PIN');
      }
    } catch {
      toast.error('Failed to remove outlet PIN');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC] p-8">
      <div className="max-w-3xl mx-auto space-y-8 animate-fade-in">

        {/* Header */}
        <div className="space-y-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-700 mb-4 transition-colors"
          >
            ← Back to Settings
          </button>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Access Control</h1>
          <p className="text-sm text-slate-500 font-medium">
            Manage outlet login PIN, report protection, and role-based access
          </p>
        </div>

        {/* ── OUTLET PIN CARD ──────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-green-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Key className="text-green-600" size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Outlet Login PIN</h3>
                  <p className="text-xs text-slate-500 mt-0.5 font-semibold">
                    A 4-digit PIN for quick daily login to this outlet.
                    {hasOutletPin
                      ? <span className="ml-1 text-green-600">● Active</span>
                      : <span className="ml-1 text-slate-400">Not set</span>
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {hasOutletPin ? (
                  <>
                    <button
                      onClick={openSetFlow}
                      className="px-3 py-1.5 text-xs font-bold text-green-700 border border-green-200 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      Change PIN
                    </button>
                    <button
                      onClick={openRemoveFlow}
                      className="px-3 py-1.5 text-xs font-bold text-red-600 border border-red-200 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <button
                    onClick={openSetFlow}
                    className="px-4 py-2 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <Key size={13} /> Set Outlet PIN
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="px-5 py-3 bg-slate-50/50">
            <p className="text-xs text-slate-400 font-medium leading-relaxed">
              This PIN is specific to this outlet only. Staff can use it to log in quickly without typing the full password.
              It does not affect Admin or Manager access.
            </p>
          </div>
        </div>

        {/* ── REPORT PROTECTION + ADMIN / MANAGER PIN CARD ─────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Toggle */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Shield className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Enable PIN Protection for Reports</h3>
                  <p className="text-xs text-slate-500 mt-1 font-medium">
                    Require PIN authentication to access sales, expense, GST, and analytics reports.
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggleProtection(!pinProtectionEnabled)}
                className={`relative w-14 h-7 rounded-full transition-colors duration-200 focus:outline-none ${
                  pinProtectionEnabled ? 'bg-blue-600' : 'bg-slate-200'
                }`}
              >
                <div className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200 flex items-center justify-center ${
                  pinProtectionEnabled ? 'translate-x-7' : 'translate-x-0'
                }`}>
                  {pinProtectionEnabled && <CheckCircle2 className="text-blue-600" size={14} />}
                </div>
              </button>
            </div>
          </div>

          {/* Admin PIN Settings */}
          <div className="p-6 border-b border-slate-100 bg-gradient-to-br from-slate-50/50 to-white">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                <Key className="text-purple-600" size={20} />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-slate-900">Admin PIN</h4>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Full access to all reports. Shared across all outlets under your account.
                </p>
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                  <Smartphone size={13} />
                  <span className="font-medium">Verification code sent to owner's email</span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {hasAdminPin && (
                  <button
                    onClick={() => navigate('/change-admin-pin')}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition-colors flex items-center gap-1"
                  >
                    <Edit2 size={12} /> Change
                  </button>
                )}
                <button
                  onClick={() => navigate('/setup-admin-pin')}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5"
                >
                  {hasAdminPin ? 'Reset' : 'Set Admin PIN'}
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          </div>

          {/* Manager Permissions */}
          {hasAdminPin && (
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                  <UserCog className="text-blue-600" size={20} />
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">Manager Permissions Settings</h4>
                    <p className="text-xs text-slate-500 mt-1">
                      Configure what features the manager can access when they log in.
                    </p>
                  </div>

                  {/* Radio Choice */}
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="managerAccessMode"
                        checked={managerFullAccess}
                        onChange={() => setManagerFullAccess(true)}
                        className="w-4 h-4 text-blue-600 accent-blue-600 mt-0.5 cursor-pointer"
                      />
                      <div>
                        <span className="text-sm font-bold text-slate-800">Give Full Access to Manager</span>
                        <p className="text-xs text-slate-500">Manager will have access to all modules and features</p>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="managerAccessMode"
                        checked={!managerFullAccess}
                        onChange={() => setManagerFullAccess(false)}
                        className="w-4 h-4 text-blue-600 accent-blue-600 mt-0.5 cursor-pointer"
                      />
                      <div>
                        <span className="text-sm font-bold text-slate-800">Limit Manager Access</span>
                        <p className="text-xs text-slate-500">Select features to restrict manager from accessing</p>
                      </div>
                    </label>
                  </div>

                  {/* Checkbox list */}
                  {!managerFullAccess && (
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 space-y-3">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Block Features:
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        {MANAGER_FEATURES.map(feat => {
                          const isBlocked = restrictedFeatures.includes(feat.id);
                          return (
                            <label key={feat.id} className="flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition-opacity">
                              <input
                                type="checkbox"
                                checked={isBlocked}
                                onChange={() => toggleFeatureRestriction(feat.id)}
                                className="w-4 h-4 rounded text-blue-600 accent-blue-600 cursor-pointer"
                              />
                              <span className={`text-sm font-semibold ${isBlocked ? 'text-slate-900 font-bold' : 'text-slate-600'}`}>
                                {feat.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="pt-2">
                    <button
                      onClick={() => triggerVerification(handleSavePermissions)}
                      disabled={saving}
                      className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-xs font-bold rounded-xl shadow-lg shadow-blue-100 hover:shadow-xl transition-all flex items-center justify-center gap-2"
                    >
                      {saving && <Loader2 size={14} className="animate-spin" />}
                      {saving ? 'Saving Settings...' : 'Save Manager Permissions'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <Shield className="text-blue-600 shrink-0 mt-0.5" size={18} />
            <div className="text-xs text-blue-950 leading-relaxed font-medium">
              <p className="font-bold mb-1">Verification Policy</p>
              <p className="text-blue-700">
                Any changes to permissions, PINs, or access control security settings require authentication using either the Admin PIN or a secure OTP code sent to the verified owner's email address.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Verification Modal */}
      {showVerifyModal && (
        <Modal
          title="Admin Security Verification"
          subtitle="Identity verification is required to complete this action"
          accentColor="blue"
          onClose={() => {
            setShowVerifyModal(false);
            setOnVerifiedCallback(null);
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">
                Method 1: Enter Admin PIN
              </label>
              <PinBoxes value={verifyPin} onChange={setVerifyPin} color="blue" />
            </div>

            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[10px] font-black text-slate-400">OR</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">
                Method 2: Email verification OTP
              </label>
              {!verifyOtpSent ? (
                <button
                  onClick={handleSendVerifyOtp}
                  disabled={verifyLoading}
                  className="w-full py-2.5 border-2 border-blue-600 text-blue-600 bg-blue-50/50 hover:bg-blue-50 font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-1.5"
                >
                  {verifyLoading && <Loader2 size={13} className="animate-spin" />}
                  Send OTP to {adminEmail}
                </button>
              ) : (
                <div className="space-y-1.5">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={verifyOtp}
                    onChange={e => setVerifyOtp(e.target.value)}
                    placeholder="Enter 6-digit OTP code"
                    className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm font-semibold outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all bg-slate-50"
                  />
                  <p className="text-[10px] text-slate-400">Verification code sent to {adminEmail}</p>
                </div>
              )}
            </div>

            {verifyError && (
              <p className="text-xs text-red-600 font-bold text-center mt-2">{verifyError}</p>
            )}

            <button
              onClick={handleVerifyAction}
              disabled={verifyLoading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
            >
              {verifyLoading && <Loader2 size={16} className="animate-spin" />}
              {verifyLoading ? 'Verifying...' : 'Verify & Save'}
            </button>
          </div>
        </Modal>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          OUTLET PIN MODALS
      ═══════════════════════════════════════════════════════════════════════ */}

      {/* Step 1: Password verification (set flow) */}
      {outletPinModal === 'set-password' && (
        <Modal
          title={hasOutletPin ? 'Change Outlet PIN' : 'Set Outlet PIN'}
          subtitle="Verify your account password to continue"
          accentColor="green"
          onClose={closeModal}
        >
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleVerifyPassword('set-pin')}
                  placeholder="Enter your account password"
                  className="w-full px-4 py-3 pr-11 border-2 border-slate-200 rounded-xl text-sm font-semibold outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <button
              onClick={() => handleVerifyPassword('set-pin')}
              disabled={loading || !password}
              className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? 'Verifying...' : 'Continue'}
            </button>
          </div>
        </Modal>
      )}

      {/* Step 2: Enter new PIN */}
      {outletPinModal === 'set-pin' && (
        <Modal
          title="Create Outlet PIN"
          subtitle="Enter a new 4-digit PIN for this outlet"
          accentColor="green"
          onClose={closeModal}
        >
          <div className="space-y-5">
            <PinBoxes value={newPin} onChange={setNewPin} color="green" />
            <button
              onClick={() => {
                if (newPin.join('').length !== 4) { toast.error('Please enter a 4-digit PIN'); return; }
                setOutletPinModal('set-confirm');
              }}
              disabled={newPin.join('').length !== 4}
              className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </Modal>
      )}

      {/* Step 3: Confirm PIN */}
      {outletPinModal === 'set-confirm' && (
        <Modal
          title="Confirm Outlet PIN"
          subtitle="Re-enter to confirm"
          accentColor="green"
          onClose={closeModal}
        >
          <div className="space-y-5">
            <PinBoxes value={confirmPin} onChange={setConfirmPin} color="green" />
            <div className="flex gap-3">
              <button
                onClick={() => { setConfirmPin(['', '', '', '']); setOutletPinModal('set-pin'); }}
                className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all"
              >
                Back
              </button>
              <button
                onClick={handleSetOutletPin}
                disabled={loading || confirmPin.join('').length !== 4}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                {loading ? 'Saving...' : 'Set PIN'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Remove flow: password then delete */}
      {outletPinModal === 'remove-password' && (
        <Modal
          title="Remove Outlet PIN"
          subtitle="Verify your password to remove the outlet PIN"
          accentColor="red"
          onClose={closeModal}
        >
          <div className="space-y-4">
            <p className="text-xs text-red-600 font-medium bg-red-50 border border-red-100 rounded-lg p-3">
              Staff will no longer be able to log in using the outlet PIN.
            </p>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleRemoveOutletPin()}
                  placeholder="Enter your account password"
                  className="w-full px-4 py-3 pr-11 border-2 border-slate-200 rounded-xl text-sm font-semibold outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <button
              onClick={handleRemoveOutletPin}
              disabled={loading || !password}
              className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? 'Removing...' : 'Remove Outlet PIN'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PinProtectionPage;